import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentPlusIcon,
  UserPlusIcon,
  UserIcon,
  ClipboardIcon
} from '@heroicons/react/24/outline';
import { useOrganization } from '../../hooks/useOrganization';
import { useAuth } from '../../hooks/useAuth';

interface SummaryCard {
  title: string;
  value: number | string;
  icon: React.ComponentType<any>;
  color: string;
}

interface ActivityItem {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  user?: {
    name: string;
    email: string;
  };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { user } = useAuth();
  const [summaryData, setSummaryData] = useState({
    tasksCreated: 0,
    candidates: 0,
    interviewers: 0,
    currentPlan: ''
  });
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);

  useEffect(() => {
    // Fetch dashboard data
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(`/api/v1/organizations/${organization?.id}/dashboard`);
        const data = await response.json();
        setSummaryData(data.summary);
        setRecentActivity(data.recentActivity);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    if (organization?.id) {
      fetchDashboardData();
    }
  }, [organization?.id]);

  const summaryCards: SummaryCard[] = [
    {
      title: 'Tasks Created',
      value: summaryData.tasksCreated,
      icon: DocumentPlusIcon,
      color: 'bg-blue-500'
    },
    {
      title: 'Candidates',
      value: summaryData.candidates,
      icon: UserIcon,
      color: 'bg-green-500'
    },
    {
      title: 'Interviewers',
      value: summaryData.interviewers,
      icon: UserPlusIcon,
      color: 'bg-purple-500'
    },
    {
      title: 'Current Plan',
      value: summaryData.currentPlan,
      icon: ClipboardIcon,
      color: 'bg-yellow-500'
    }
  ];

  const quickActions = [
    {
      title: 'Create Template',
      icon: DocumentPlusIcon,
      onClick: () => navigate('/admin/templates/new')
    },
    {
      title: 'Invite Interviewer',
      icon: UserPlusIcon,
      onClick: () => navigate('/admin/interviewers/invite')
    },
    {
      title: 'Invite Candidate',
      icon: UserIcon,
      onClick: () => navigate('/admin/candidates/invite')
    },
    {
      title: 'Assign Task',
      icon: ClipboardIcon,
      onClick: () => navigate('/admin/tasks/assign')
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome, {user?.name || 'Admin'}
        </h1>
        <div className="flex items-center space-x-4">
          <span className="text-gray-600">
            Current Plan: <span className="font-semibold">{summaryData.currentPlan}</span>
          </span>
          <button
            onClick={() => navigate('/admin/subscription')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upgrade Plan
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {summaryCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-6 flex items-center space-x-4"
          >
            <div className={`p-3 rounded-full ${card.color} bg-opacity-10`}>
              <card.icon className={`w-6 h-6 ${card.color.replace('bg-', 'text-')}`} />
            </div>
            <div>
              <h3 className="text-gray-500 text-sm font-medium">{card.title}</h3>
              <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {recentActivity.map((activity) => (
            <div
              key={activity.id}
              className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
            >
              <div>
                <p className="text-gray-900">{activity.description}</p>
                <p className="text-sm text-gray-500">
                  {activity.user?.name} • {new Date(activity.timestamp).toLocaleDateString()}
                </p>
              </div>
              <span className="text-sm font-medium text-blue-600 capitalize">
                {activity.type}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className="flex items-center justify-center space-x-2 p-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <action.icon className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-900">{action.title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 